import { UISref } from '@uirouter/react';
import { Card, Col, Row, Typography } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { adminSections, type IMenuItem, menuItems } from '@/constants/menu';
import { authStore } from '@/stores';

const { Text, Title } = Typography;

const iconWrapperStyle: React.CSSProperties = {
  color: 'rgba(0, 0, 0, 0.45)',
  marginBottom: 12,
  fontSize: 24,
};

const sectionHeaderIconStyle: React.CSSProperties = {
  color: 'rgba(0, 0, 0, 0.45)',
  marginRight: 8,
  fontSize: 18,
};

function groupTilesBySection(tiles: IMenuItem[]): Map<string, IMenuItem[]> {
  const map = new Map<string, IMenuItem[]>();
  for (const item of tiles) {
    const key = item.sectionKey ?? 'other';
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(item);
  }
  return map;
}

function renderTile(item: IMenuItem) {
  return (
    <Col key={item.key} xs={24} sm={12} md={8} lg={6}>
      <UISref to={item.state}>
        <div
          style={{ display: 'block', height: '100%', cursor: 'pointer' }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              (e.currentTarget as HTMLDivElement).click();
            }
          }}
        >
          <Card hoverable style={{ height: '100%' }}>
            {item.icon && <div style={iconWrapperStyle}>{item.icon}</div>}
            <Text strong style={{ display: 'block', marginBottom: item.descriptionMessageId ? 4 : 0 }}>
              <FormattedMessage id={item.messageId} />
            </Text>
            {item.descriptionMessageId && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <FormattedMessage id={item.descriptionMessageId} />
              </Text>
            )}
          </Card>
        </div>
      </UISref>
    </Col>
  );
}

export const Admin = () => {
  const adminTiles = useMemo(
    () => menuItems.filter((item) => item.parent === 'admin' && authStore.hasPermissions(item.permissions)),
    [],
  );
  const tilesBySection = useMemo(() => groupTilesBySection(adminTiles), [adminTiles]);

  return (
    <div style={{ padding: 24 }}>
      {adminSections.map((section) => {
        const items = tilesBySection.get(section.key);
        if (!items || items.length === 0) {
          return null;
        }
        return (
          <div key={section.key} style={{ marginBottom: 32 }}>
            <Title level={5} style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
              {section.icon && <span style={sectionHeaderIconStyle}>{section.icon}</span>}
              <FormattedMessage id={section.messageId} />
            </Title>
            <Row gutter={[16, 16]}>{items.map(renderTile)}</Row>
          </div>
        );
      })}
      {tilesBySection.get('other') && tilesBySection.get('other')!.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            <FormattedMessage id="admin.section.other" />
          </Title>
          <Row gutter={[16, 16]}>{tilesBySection.get('other')!.map(renderTile)}</Row>
        </div>
      )}
    </div>
  );
};
